import {Component, EventEmitter, Injector, Input, Output} from '@angular/core'
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {DropdownComponent} from '../../utils/dropdown-component';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {
  ComponentFieldModel,
  ConfigurableComponent,
  FieldProps,
  Language,
  LanguageSelector,
  SelectItem
} from '@twpub/core/models';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import _ from 'lodash';
import {map, switchMap} from 'rxjs/operators';
import {DictionaryService} from '@twpub/core/services';

import {MultiSelectComponent} from '../multi-select/multi-select.component';

const defaultLanguageOptions = (injector: Injector) => {
  const dictService = injector.get(DictionaryService);
  return dictService.getDictionaries(true).pipe(
    map(dicts => (dicts || []).map(d => d.id)),
    switchMap(dictIds => dictService.getLanguages(dictIds)),
    map(langs => (langs || []).map(l => ({value: l.code, label: l.name})))
  );
};

@Component({
  selector: 'pub-language-dropdown',
  templateUrl: './language-dropdown.component.html',
  styleUrls: ['./language-dropdown.component.scss'],
  standalone: true,
  imports: [MultiSelectComponent]
})
export class LanguageDropdownComponent extends BaseConfigurableComponent implements LanguageSelector {
  static readonly compName = 'LanguageDropdownComponent';

  @Input() langs: Language[] = [];
  @Input() selectedLangCodes?: string[];

  @Output() selectLanguages: EventEmitter<string[]> = new EventEmitter<string[]>()
  multipleSelect: boolean = false;
  allowAllSelection: boolean = false;
  allSelectedLabel?: string;

  items: SelectItem[] = [];
  selectedValues?: string[];

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [this.LABEL_FIELD, this.VISIBLE_FIELD,
      {
        key: DropdownComponent.MULTIPLE_SELECT,
        label: 'Multiple selections',
        controlType: ControlType.Boolean,
        description: 'Allow multiple selections in dropdown'
      },
      {
        key: DropdownComponent.INCLUDE_ALL_OPTION,
        label: 'Include "All Languages" option',
        controlType: ControlType.Boolean,
        description: 'Include option for "All Languages" in single-select dropdown'
      },
      {
        key: DropdownComponent.DEFAULT_VALUE,
        label: 'Default language code',
        controlType: ControlType.Dropdown,
        options: defaultLanguageOptions
      }
    ];

    constructor() {
      super(LanguageDropdownComponent, LanguageDropdownComponent.compName, 'Dropdown List', [ComponentCategory.SourceLanguageSelector, ComponentCategory.TargetLanguageSelector]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<LanguageSelector>(LanguageDropdownComponent, this.name, initialConfig);
    }

    override getFields(compConfig: ComponentConfiguration<ConfigurableComponent>): ComponentFieldModel<any>[] {
      const fields = super.getFields(compConfig);
      return DropdownComponent.getFields(compConfig, fields);
    }
  }

  override doUpdate() {
    super.doUpdate();
    this.multipleSelect = !!this.config?.getBoolean(DropdownComponent.MULTIPLE_SELECT);
    this.allowAllSelection = !!this.config?.getBoolean(DropdownComponent.INCLUDE_ALL_OPTION);
    this.allSelectedLabel = this.multipleSelect || this.allowAllSelection ? $localize`:@@languageDropdown.allLanguages:All Languages` : undefined;

    // Check if languages come from multiple dictionaries
    const uniqueDictIds = new Set(this.langs.map(l => l.dictionaryId));
    const fromMultipleDicts = uniqueDictIds.size > 1;

    let langsToProcess = this.langs;
    if (fromMultipleDicts) {
      // Sort alphabetically by name when from multiple dictionaries
      langsToProcess = [...this.langs].sort((a, b) => a.name.localeCompare(b.name));
    }

    // Find duplicate language names
    const nameCounts = new Map<string, number>();
    langsToProcess.forEach(lang => {
      nameCounts.set(lang.name, (nameCounts.get(lang.name) || 0) + 1);
    });

    // Create items with language code in parentheses only for duplicates
    this.items = langsToProcess.map(lang => {
      const isDuplicate = (nameCounts.get(lang.name) || 0) > 1;
      const label = isDuplicate ? `${lang.name} (${lang.code})` : lang.name;
      return new SelectItem(lang.code, label);
    });

    let newSelectedLangs = this.filterExisting(this.selectedLangCodes);
    if (!newSelectedLangs?.length) {
      if (this.selectedValues !== undefined && !this.selectedValues.length && this.allowAllSelection) {
        // User previously chose "All Languages" — preserve that selection
        newSelectedLangs = [];
      } else {
        newSelectedLangs = this.setDefaultLanguages();
      }
    }
    if (!newSelectedLangs?.length) {
      newSelectedLangs = this.allowAllSelection ? [] : this.selectFirstAvailable();
    }
    if (!_.isEqual(newSelectedLangs, this.selectedValues)) {
      this.selectedValues = newSelectedLangs;
      this.selectLanguages.emit(newSelectedLangs);
    }
  }

  private setDefaultLanguages = () => {
    const defaultLangs = this.filterExisting(this.config?.getStringArray(DropdownComponent.DEFAULT_VALUE, []));
    return this.multipleSelect ? defaultLangs : defaultLangs?.slice(0, 1);
  }

  private selectFirstAvailable = () => {
    return this.langs?.[0] ? [this.langs[0].code] : [];
  }

  private filterExisting = (langs: string[] | undefined) => {
    if (!langs?.length) return undefined;
    const validLanguages = langs.filter(code =>
      this.langs?.some(l => l.code === code)
    );
    return validLanguages.length < langs.length ? validLanguages : langs;
  };

  onSelectValues(selectedValues: string[]) {
    this.logger.debug('LanguageDropdownComponent.onSelectValues:', {selectedValues})
    this.selectLanguages.emit(selectedValues);
  }
}
